import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';
import logoImage from '../images/images.png'; // Update this path to your logo// 
// // // Create refined styles inspired by Tailwind
const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    color: '#374151', // Tailwind gray-700
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 30,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB', // Tailwind gray-200
  },
  headerTextContainer: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827', // Tailwind gray-900
    marginBottom: 4,
  },
  headerSubtext: {
    fontSize: 14,
    color: '#4F46E5', // Tailwind indigo-600
    fontWeight: 'medium',
  },
  logo: {
    width: 100,
    height: 40,
    objectFit: 'contain',
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    marginTop: 24,
    color: '#111827', // Tailwind gray-900
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB', // Tailwind gray-200
  },
  firstSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#111827', // Tailwind gray-900
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB', // Tailwind gray-200
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#E5E7EB', // Tailwind gray-200
    borderRadius: 6,
    marginBottom: 16,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB', // Tailwind gray-200
    minHeight: 32,
  },
  tableRowLast: {
    flexDirection: 'row',
    minHeight: 32,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#F3F4F6', // Tailwind gray-100
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB', // Tailwind gray-200
    paddingBottom: 4, // Extra padding at the bottom of the header row
  },
  tableHeaderCell: {
    padding: 10,
    paddingBottom: 12,
    color: '#4B5563', // Tailwind gray-600
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'right',
    flex: 1,
  },
  tableHeaderCellLeft: {
    padding: 10,
    paddingBottom: 12,
    color: '#4B5563', // Tailwind gray-600
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'left',
    width: '40%',
  },
  tableLabelCell: {
    width: '40%',
    padding: 10,
    fontSize: 12,
    color: '#4B5563', // Tailwind gray-600
    textAlign: 'left',
  },
  tableValueCell: {
    flex: 1,
    padding: 10,
    fontSize: 12,
    color: '#111827', // Tailwind gray-900
    textAlign: 'right',
  },
  boldCell: {
    fontWeight: 'bold',
  },
  indentedCell: {
    paddingLeft: 24,
    color: '#6B7280', // Tailwind gray-500
  },
  highlightRow: {
    backgroundColor: '#F9FAFB', // Tailwind gray-50
  },
  redText: {
    color: '#EF4444', // Tailwind red-500
  },
  greenText: {
    color: '#10B981', // Tailwind green-500
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 10,
    textAlign: 'center',
    color: '#9CA3AF', // Tailwind gray-400
  },
  pageNumber: {
    position: 'absolute',
    bottom: 30,
    right: 40,
    fontSize: 10,
    color: '#9CA3AF', // Tailwind gray-400
  },
});

// Simple PDF document component
const PrequalSummaryDocument = ({ data }) => {
  const {
    businessName = 'N/A',
    purchasePrice = 0,
    prequalifiedAmount = 0,
    prequalificationType = 'Easy Financing',
    ocrData = {}
  } = data;

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format percentage
  const formatPercent = (value) => {
    return `${value.toFixed(2)}%`;
  };

  // Calculate estimated fees
  const calculateGuaranteeFee = (loanAmount) => {
    const guaranteedPortion = loanAmount * 0.75; // Assuming 75% is guaranteed by SBA
    
    if (loanAmount <= 1000000) {
      return 0; // 0% for loans $1,000,000 or less
    } else if (loanAmount <= 2000000) {
      // 1.45% of guaranteed portion up to $1M + 1.7% of guaranteed portion over $1M
      const firstMillion = Math.min(guaranteedPortion, 1000000);
      const overFirstMillion = Math.max(0, guaranteedPortion - 1000000);
      return (firstMillion * 0.0145) + (overFirstMillion * 0.017);
    } else {
      // 3.5% of guaranteed portion up to $1M + 3.75% of guaranteed portion over $1M
      const firstMillion = Math.min(guaranteedPortion, 1000000);
      const overFirstMillion = Math.max(0, guaranteedPortion - 1000000);
      return (firstMillion * 0.035) + (overFirstMillion * 0.0375);
    }
  };
  
  // Fixed fees
  const packagingFee = 2500;
  const closingCosts = 10000;
  
  // Calculate loan components based on prequalified amount
  const totalPrequalifiedAmount = prequalifiedAmount;
  
  // Calculate total fees
  const estimatedLoanAmount = totalPrequalifiedAmount * 0.9; // 90% of prequalified amount
  const estimatedGuaranteeFee = calculateGuaranteeFee(estimatedLoanAmount);
  const totalFees = estimatedGuaranteeFee + packagingFee + closingCosts;
  
  // Calculate actual purchase price (prequalified amount minus fees)
  const actualPurchasePrice = totalPrequalifiedAmount - totalFees;
  
  // Calculate equity injection (10% of prequalified amount)
  const equityInjection = totalPrequalifiedAmount * 0.1;
  
  // Calculate loan amount (90% of prequalified amount)
  const loanAmount = totalPrequalifiedAmount * 0.9;
  
  // Interest rate based on prequalification type
  const baseRate = 7.5; // Current prime rate
  const interestRateAddon = prequalificationType === 'Easy Financing' ? 2.0 : 2.5;
  const interestRate = baseRate + interestRateAddon;
  
  // Calculate monthly payment
  const loanTerm = 10; // 10-year term
  const monthlyRate = interestRate / 100 / 12;
  const numberOfPayments = loanTerm * 12;
  
  const calculateMonthlyPayment = () => {
    if (loanAmount <= 0 || monthlyRate <= 0) {
      return 0;
    }
    return (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
           (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  };
  
  const monthlyPayment = calculateMonthlyPayment();
  const annualDebtService = monthlyPayment * 12;
  
  // Get years from OCR data
  const years = Object.keys(ocrData).sort((a, b) => {
    // Sort years in chronological order
    return a.localeCompare(b);
  });

  // Financial metrics calculation functions
  const safeNumber = (value) => {
    const num = parseFloat(value);
    return isNaN(num) || !isFinite(num) ? 0 : num;
  };

  const calculateEBITDA = (yearData) => {
    if (!yearData) return 0;
    
    const income = safeNumber(yearData.ordinary_business_income);
    const officerComp = safeNumber(yearData.compensation_of_officers);
    const interest = safeNumber(yearData.interest);
    const depreciation = safeNumber(yearData.depreciation);
    const amortization = safeNumber(yearData.amortization);
    
    return income + officerComp + interest + depreciation + amortization;
  };

  const calculateAddBacks = (yearData) => {
    if (!yearData || !yearData.other_deductions) return 0;
    
    if (Array.isArray(yearData.other_deductions)) {
      return yearData.other_deductions.reduce((total, item) => {
        return total + safeNumber(item.amount);
      }, 0);
    }
    
    return 0;
  };

  const calculateSDE = (yearData) => {
    if (!yearData) return 0;
    return calculateEBITDA(yearData) + calculateAddBacks(yearData);
  };

  const calculateNOI = (yearData) => {
    if (!yearData) return 0;
    const sde = calculateSDE(yearData);
    
    // Use the new_officer_compensation from the data if available, otherwise use the default
    // This matches the logic in PrequalFinancialMetricsDisplay.js
    const newOfficerCompensation = Number(yearData.new_officer_compensation || 
                                          (prequalificationType === 'Easy Financing' ? 75000 : 0));
    
    return sde - newOfficerCompensation;
  };

  const calculateDSCR = (yearData) => {
    const noi = calculateNOI(yearData);
    return annualDebtService > 0 ? noi / annualDebtService : 0;
  };

  // Updated Add-Backs calculation to use actual names
  const getAddBacksList = (yearData) => {
    if (!yearData || !yearData.other_deductions) return [];
    
    if (Array.isArray(yearData.other_deductions)) {
      return yearData.other_deductions.map(item => ({
        name: item.description || item.name || 'Other Deduction',
        amount: safeNumber(item.amount)
      }));
    }
    
    return [];
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with Logo and Company Name */}
        <View style={styles.header}>
          <View style={styles.headerTextContainer}>
            <Text style={styles.companyName}>{businessName}</Text>
            <Text style={styles.headerSubtext}>Prequalification Summary</Text>
          </View>
          <Image style={styles.logo} src={logoImage} />
        </View>

        {/* Purchase Price Breakdown */}
        <View style={styles.section}>
          <Text style={styles.firstSectionTitle}>Purchase Price Breakdown</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Prequalified Amount:</Text>
              <Text style={[styles.tableValueCell, styles.boldCell]}>{formatCurrency(totalPrequalifiedAmount)}</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.indentedCell]}>SBA Guarantee Fee:</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(estimatedGuaranteeFee)}</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.indentedCell]}>Packaging Fee:</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(packagingFee)}</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.indentedCell]}>Closing Costs:</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(closingCosts)}</Text>
            </View>
            
            <View style={[styles.tableRowLast, styles.highlightRow]}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Actual Purchase Price:</Text>
              <Text style={[styles.tableValueCell, styles.boldCell]}>{formatCurrency(actualPurchasePrice)}</Text>
            </View>
          </View>
          
          {/* Loan Summary */}
          <Text style={styles.sectionTitle}>Loan Summary</Text>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>Equity Injection (10%):</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(equityInjection)}</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>Loan Amount (90%):</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(loanAmount)}</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>Interest Rate:</Text>
              <Text style={styles.tableValueCell}>Prime + {interestRateAddon}% ({interestRate}%)</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={styles.tableLabelCell}>Loan Term:</Text>
              <Text style={styles.tableValueCell}>{loanTerm} years</Text>
            </View>
            
            <View style={styles.tableRow}>
              <Text style={[styles.tableLabelCell, styles.boldCell]}>Monthly Payment:</Text>
              <Text style={[styles.tableValueCell, styles.boldCell]}>{formatCurrency(monthlyPayment)}</Text>
            </View>
            
            <View style={styles.tableRowLast}>
              <Text style={styles.tableLabelCell}>Annual Debt Service:</Text>
              <Text style={styles.tableValueCell}>{formatCurrency(annualDebtService)}</Text>
            </View>
          </View>
        </View>

        {/* Financial Metrics */}
        {years.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Financial Metrics</Text>
            
            <View style={styles.table}>
              {/* Table Header with Extra Height */}
              <View style={[styles.tableHeaderRow, { minHeight: 30 }]}>
                <Text style={styles.tableHeaderCellLeft}>Metric</Text>
                {years.map((year, idx) => (
                  <Text key={idx} style={styles.tableHeaderCell}>
                    {year.replace('Business Tax Return - ', '')}
                  </Text>
                ))}
              </View>
              
              {/* Gross Receipts */}
              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>Gross Receipts or Sales</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const grossReceipts = yearData.gross_receipts_or_sales || yearData.gross_receipts || 0;
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(grossReceipts)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Cost of Goods Sold */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, styles.redText]}>Cost of Goods Sold</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const cogs = yearData.cost_of_goods_sold || 0;
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.redText]}>
                      {formatCurrency(cogs)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Ordinary Business Income */}
              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>Ordinary Business Income</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const income = yearData.ordinary_business_income || 0;
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(income)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Officer Compensation */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, { paddingLeft: 20 }]}>+ Original Officer Compensation</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const officerComp = yearData.compensation_of_officers || 0;
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(officerComp)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Interest */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, { paddingLeft: 20 }]}>+ Interest</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const interest = yearData.interest || 0;
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(interest)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Depreciation */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, { paddingLeft: 20 }]}>+ Depreciation</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const depreciation = yearData.depreciation || 0;
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(depreciation)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Amortization */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, { paddingLeft: 20 }]}>+ Amortization</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const amortization = yearData.amortization || 0;
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(amortization)}
                    </Text>
                  );
                })}
              </View>
              
              {/* EBITDA */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, styles.boldCell]}>EBITDA</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const ebitda = calculateEBITDA(yearData);
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.boldCell]}>
                      {formatCurrency(ebitda)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Add-Backs Header Row */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, { paddingLeft: 20 }]}>+ Add-Backs:</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const addBacks = calculateAddBacks(yearData);
                  return (
                    <Text key={idx} style={styles.tableValueCell}>
                      {formatCurrency(addBacks)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Individual Add-Backs Items - Updated to use actual names */}
              {years.length > 0 && years.some(year => {
                const yearData = ocrData[year] || {};
                return getAddBacksList(yearData).length > 0;
              }) && years.map(year => {
                const yearData = ocrData[year] || {};
                const addBacksList = getAddBacksList(yearData);
                
                return addBacksList.map((item, itemIdx) => (
                  <View key={`${year}-addback-${itemIdx}`} style={styles.tableRow}>
                    <Text style={[styles.tableLabelCell, { paddingLeft: 36, color: '#6B7280' }]}>
                      {item.name}
                    </Text>
                    {years.map((yearCol, yearIdx) => (
                      <Text key={yearIdx} style={styles.tableValueCell}>
                        {yearCol === year ? formatCurrency(item.amount) : ''}
                      </Text>
                    ))}
                  </View>
                ));
              })}
              
              {/* SDE */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, styles.boldCell]}>Seller's Discretionary Earnings (SDE)</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const sde = calculateSDE(yearData);
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.boldCell]}>
                      {formatCurrency(sde)}
                    </Text>
                  );
                })}
              </View>
              
              {/* New Officer Compensation */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, styles.redText, { paddingLeft: 20 }]}>- New Officer Compensation</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  // Use the new_officer_compensation from the data if available, otherwise use the default
                  // based on prequalification type
                  const newOfficerComp = yearData.new_officer_compensation || 
                                        (prequalificationType === 'Easy Financing' ? 75000 : 0);
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.redText]}>
                      {formatCurrency(newOfficerComp)}
                    </Text>
                  );
                })}
              </View>
              
              {/* NOI */}
              <View style={styles.tableRow}>
                <Text style={[styles.tableLabelCell, styles.boldCell]}>Net Operating Income (NOI)</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const noi = calculateNOI(yearData);
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.boldCell]}>
                      {formatCurrency(noi)}
                    </Text>
                  );
                })}
              </View>
              
              {/* Annual Debt Service */}
              <View style={styles.tableRow}>
                <Text style={styles.tableLabelCell}>Annual Debt Service</Text>
                {years.map((year, idx) => (
                  <Text key={idx} style={styles.tableValueCell}>
                    {formatCurrency(annualDebtService)}
                  </Text>
                ))}
              </View>
              
              {/* DSCR */}
              <View style={styles.tableRowLast}>
                <Text style={[styles.tableLabelCell, styles.boldCell]}>Debt Service Coverage Ratio</Text>
                {years.map((year, idx) => {
                  const yearData = ocrData[year] || {};
                  const dscr = calculateDSCR(yearData);
                  const dscrColor = dscr >= 1.15 ? styles.greenText : styles.redText;
                  return (
                    <Text key={idx} style={[styles.tableValueCell, styles.boldCell, dscrColor]}>
                      {dscr.toFixed(2)}
                    </Text>
                  );
                })}
              </View>
            </View>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          Generated on {new Date().toLocaleDateString()} | Lended Prequalification Summary
        </Text>
        
        <Text style={styles.pageNumber} render={({ pageNumber, totalPages }) => (
          `${pageNumber} / ${totalPages}`
        )} fixed />
      </Page>
    </Document>
  );
};

export const generatePrequalSummaryPDF = async (data) => {
  try {
    const blob = await pdf(<PrequalSummaryDocument data={data} />).toBlob();
    return blob;
  } catch (error) {
    console.error('Error generating prequalification PDF:', error);
    throw error;
  }
};